import { useState, useEffect } from 'react';
import { ListChecks, FlaskConical, Check, Thermometer, Filter, Droplets } from 'lucide-react';
import { useLab } from '../../../context/LabContext';
import { Beaker, SoilContainer } from '../../lab';
import InfoBox from '../InfoBox';

const steps = [
  { id: 1, title: 'Weigh Soil Sample', short: 'Click balance to weigh' },
  { id: 2, title: 'Transfer to Beaker', short: 'Add soil to beaker' },
  { id: 3, title: 'Add Hydrochloric Acid', short: 'Add 5 mL conc. HCl + 5 mL H₂O' },
  { id: 4, title: 'Simmer on Hot Plate', short: 'Simmer gently for 15 min' },
  { id: 5, title: 'Filter Hot', short: 'Filter the mixture' },
  { id: 6, title: 'Dilute to Volume', short: 'Dilute to 100 mL' }
];

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'Sample Weighing',
    content: 'We weigh approximately 1.0 g of dried soil sample for extraction. Accurate weighing is crucial for calculating the final iron concentration in the soil.'
  },
  3: {
    title: 'Iron Extraction',
    content: 'Add 5 mL of concentrated HCl and 5 mL of water to the beaker containing the soil. Hydrochloric acid dissolves iron oxides and hydroxides in the soil, converting them to soluble iron(III) chloride.',
    chemicalEquation: 'Fe2O3(s) + 6HCl(aq) -> 2FeCl3(aq) + 3H₂O(l)'
  },
  4: {
    title: 'Extraction Time (15 minutes)',
    content: 'Simmer gently for 15 minutes to extract iron from the soil matrix. Cover with a watch glass to minimize evaporation. Do NOT boil vigorously -- a gentle simmer is sufficient.'
  },
  5: {
    title: 'Filtration',
    content: 'Filtering removes insoluble soil particles (silicates, organic matter) while allowing the dissolved iron(III) chloride to pass through. The filtrate contains the extracted iron.'
  }
};

// Hot Plate Component
const HotPlate = ({
  isOn = false,
  temperature = 25,
  onClick,
  active = false,
  disabled = false
}: {
  isOn?: boolean;
  temperature?: number;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
}) => {
  return (
    <svg
      viewBox="0 0 140 100"
      width={130}
      height={90}
      onClick={disabled ? undefined : onClick}
      style={{
        cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1
      }}
    >
      <defs>
        <linearGradient id="hot-plate-body" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#4A4A4A" />
          <stop offset="100%" stopColor="#2A2A2A" />
        </linearGradient>
        <radialGradient id="heating-element" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={isOn ? '#FF4500' : '#3A3A3A'} />
          <stop offset="70%" stopColor={isOn ? '#CC3300' : '#2A2A2A'} />
          <stop offset="100%" stopColor="#1A1A1A" />
        </radialGradient>
      </defs>

      {/* Active glow */}
      {active && (
        <rect
          x="5" y="15" width="130" height="75" rx="8"
          fill="none"
          stroke="var(--primary)"
          strokeWidth="3"
          style={{ animation: 'glow 1.5s ease-in-out infinite' }}
        />
      )}

      {/* Base */}
      <rect x="10" y="20" width="120" height="70" rx="6" fill="url(#hot-plate-body)" />

      {/* Heating surface */}
      <rect x="20" y="30" width="100" height="50" rx="4" fill="#1A1A1A" />

      {/* Heating element */}
      <circle cx="70" cy="55" r="30" fill="url(#heating-element)">
        {isOn && (
          <animate
            attributeName="opacity"
            values="1;0.7;1"
            dur="1s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      {/* Heating coil pattern */}
      <g stroke={isOn ? '#FF6347' : '#4A4A4A'} strokeWidth="2" fill="none">
        <circle cx="70" cy="55" r="10" />
        <circle cx="70" cy="55" r="18" />
        <circle cx="70" cy="55" r="26" />
      </g>

      {/* Control knob */}
      <circle cx="125" cy="75" r="8" fill="#3A3A3A" stroke="#5A5A5A" strokeWidth="1" />
      <line
        x1="125" y1="75"
        x2="125" y2={75 - 6}
        stroke={isOn ? '#FF4500' : '#8A8A8A'}
        strokeWidth="2"
        strokeLinecap="round"
        transform={`rotate(${isOn ? 90 : 0}, 125, 75)`}
      />

      {/* Temperature display */}
      <rect x="15" y="22" width="35" height="12" rx="2" fill="#0A0A0A" />
      <text x="32" y="31" fontSize="8" fill={isOn ? '#FF4500' : '#00FF00'} textAnchor="middle" fontFamily="monospace">
        {temperature}C
      </text>

      {/* Label */}
      <text x="70" y="95" fontSize="10" fill="var(--text-primary)" textAnchor="middle">
        Hot Plate
      </text>
    </svg>
  );
};

// Filtration Apparatus Component
const FiltrationApparatus = ({
  isFiltering = false,
  progress = 0,
  onClick,
  active = false,
  disabled = false
}: {
  isFiltering?: boolean;
  progress?: number;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
}) => {
  return (
    <svg
      viewBox="0 0 100 140"
      width={90}
      height={130}
      onClick={disabled ? undefined : onClick}
      style={{
        cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1
      }}
    >
      {/* Active glow */}
      {active && (
        <rect
          x="10" y="5" width="80" height="125" rx="8"
          fill="none"
          stroke="var(--primary)"
          strokeWidth="3"
          style={{ animation: 'glow 1.5s ease-in-out infinite' }}
        />
      )}

      {/* Ring stand */}
      <rect x="45" y="20" width="10" height="100" fill="#5A5A5A" />
      <rect x="20" y="115" width="60" height="8" rx="2" fill="#4A4A4A" />

      {/* Funnel */}
      <path
        d="M30 35 L50 70 L50 85 L50 70 L70 35 Z"
        fill="#E8E8E8"
        stroke="#B8B8B8"
        strokeWidth="1.5"
      />

      {/* Filter paper in funnel */}
      <path
        d="M32 37 L50 68 L68 37 Z"
        fill="#FFF8DC"
        stroke="#DDD8BC"
        strokeWidth="1"
      />

      {/* Liquid in funnel (brown extraction) */}
      {(isFiltering || progress > 0) && (
        <path
          d="M35 42 L50 62 L65 42 Z"
          fill="#8B7355"
          opacity={1 - progress / 100}
        />
      )}

      {/* Receiving flask */}
      <path
        d="M35 90 L35 120 Q35 125 40 125 L60 125 Q65 125 65 120 L65 90 Q65 85 50 85 Q35 85 35 90 Z"
        fill="rgba(200, 220, 240, 0.4)"
        stroke="#7A9BB8"
        strokeWidth="1.5"
      />

      {/* Collected filtrate */}
      {progress > 0 && (
        <rect
          x="37"
          y={125 - (progress * 0.35)}
          width="26"
          height={progress * 0.35}
          fill="#D4A574"
          opacity="0.8"
        />
      )}

      {/* Dripping animation */}
      {isFiltering && (
        <circle cx="50" cy="75" r="2" fill="#8B7355">
          <animate
            attributeName="cy"
            values="75;85;75"
            dur="0.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="1;0;1"
            dur="0.5s"
            repeatCount="indefinite"
          />
        </circle>
      )}

      {/* Label */}
      <text x="50" y="138" fontSize="9" fill="var(--text-primary)" textAnchor="middle">
        Filter
      </text>
    </svg>
  );
};

// Volumetric Flask Component
const VolumetricFlask = ({
  volume = 100,
  fillLevel = 0,
  fillColor = '#D4A574',
  label = '',
  onClick,
  active = false,
  disabled = false
}: {
  volume?: number;
  fillLevel?: number;
  fillColor?: string;
  label?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
}) => {
  return (
    <svg
      viewBox="0 0 80 130"
      width={70}
      height={120}
      onClick={disabled ? undefined : onClick}
      style={{
        cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1
      }}
    >
      {/* Active glow */}
      {active && (
        <ellipse
          cx="40" cy="80" rx="35" ry="45"
          fill="none"
          stroke="var(--primary)"
          strokeWidth="3"
          style={{ animation: 'glow 1.5s ease-in-out infinite' }}
        />
      )}

      {/* Flask body - pear shape */}
      <path
        d="M32 10 L32 35 Q10 50 10 80 Q10 115 40 115 Q70 115 70 80 Q70 50 48 35 L48 10 Z"
        fill="rgba(200, 220, 240, 0.3)"
        stroke="#7A9BB8"
        strokeWidth="2"
      />

      {/* Liquid fill */}
      {fillLevel > 0 && (
        <path
          d={`M12 ${115 - fillLevel * 0.7} Q12 ${115 - fillLevel * 0.7 + 5} 40 ${115 - fillLevel * 0.7 + 5} Q68 ${115 - fillLevel * 0.7 + 5} 68 ${115 - fillLevel * 0.7} L68 80 Q68 112 40 112 Q12 112 12 80 Z`}
          fill={fillColor}
          opacity="0.7"
        />
      )}

      {/* Calibration mark at 100 mL */}
      <line x1="25" y1="45" x2="35" y2="45" stroke="#5A7A95" strokeWidth="1.5" />
      <text x="22" y="48" fontSize="7" fill="#5A7A95" textAnchor="end">{volume}</text>

      {/* Neck */}
      <rect x="32" y="5" width="16" height="8" fill="rgba(200, 220, 240, 0.4)" stroke="#7A9BB8" strokeWidth="1" />

      {/* Glass shine */}
      <line x1="15" y1="55" x2="15" y2="100" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="2" />

      {/* Label */}
      <text x="40" y="125" fontSize="9" fill="var(--text-primary)" textAnchor="middle">
        {label || `${volume}mL`}
      </text>
    </svg>
  );
};

// Analytical Balance Component
const AnalyticalBalance = ({
  reading = 0,
  isTared = false,
  onClick,
  active = false,
  disabled = false
}: {
  reading?: number;
  isTared?: boolean;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
}) => {
  return (
    <svg
      viewBox="0 0 140 100"
      width={130}
      height={90}
      onClick={disabled ? undefined : onClick}
      style={{
        cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1
      }}
    >
      {/* Active glow */}
      {active && (
        <rect
          x="5" y="5" width="130" height="85" rx="8"
          fill="none"
          stroke="var(--primary)"
          strokeWidth="3"
          style={{ animation: 'glow 1.5s ease-in-out infinite' }}
        />
      )}

      {/* Body */}
      <rect x="10" y="10" width="120" height="75" rx="6" fill="#E8E8E8" stroke="#CCCCCC" strokeWidth="2" />

      {/* Display */}
      <rect x="20" y="18" width="100" height="25" rx="3" fill="#001a00" />
      <text x="115" y="36" fontSize="16" fill="#00FF00" textAnchor="end" fontFamily="monospace">
        {reading.toFixed(4)} g
      </text>

      {/* Weighing chamber */}
      <rect x="25" y="50" width="90" height="28" rx="2" fill="#F8F8F8" stroke="#DDDDDD" strokeWidth="1" />

      {/* Pan */}
      <ellipse cx="70" cy="64" rx="35" ry="8" fill="#CCCCCC" />

      {/* Tare indicator */}
      {isTared && (
        <circle cx="25" y="25" r="4" fill="#00FF00" />
      )}

      {/* Label */}
      <text x="70" y="95" fontSize="9" fill="var(--text-primary)" textAnchor="middle">
        Balance
      </text>
    </svg>
  );
};

const Part1Extraction = () => {
  const { state, updateExp9Part1, completeStep, update } = useLab();
  const { exp9Part1, currentStep, labMode, completedSteps } = state;

  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [isWeighing, setIsWeighing] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterProgress, setFilterProgress] = useState(0);

  // Fix: Reset currentStep to first incomplete step when this part mounts
  useEffect(() => {
    if (labMode !== 'guided') return;
    const firstIncomplete = steps.find(s => !completedSteps.includes(`extraction-${s.id}`));
    const targetStep = firstIncomplete ? firstIncomplete.id : steps[steps.length - 1].id;
    if (currentStep !== targetStep) {
      update({ currentStep: targetStep });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (s: number) => labMode === 'free' || currentStep === s;
  const isDone = (s: number) => completedSteps.includes(`extraction-${s}`);

  // Heating progress effect
  useEffect(() => {
    if (!exp9Part1.heated && exp9Part1.acidAdded && exp9Part1.heatingProgress > 0 && exp9Part1.heatingProgress < 100) {
      const timer = setInterval(() => {
        const newProgress = Math.min(exp9Part1.heatingProgress + 2, 100);
        updateExp9Part1({ heatingProgress: newProgress });

        if (newProgress >= 100) {
          updateExp9Part1({ heated: true });
          completeStep('extraction-4');
        }
      }, 100);

      return () => clearInterval(timer);
    }
  }, [exp9Part1.heatingProgress, exp9Part1.heated, exp9Part1.acidAdded]);

  // Filtering progress effect
  useEffect(() => {
    if (isFiltering && filterProgress < 100) {
      const timer = setInterval(() => {
        const newProgress = Math.min(filterProgress + 5, 100);
        setFilterProgress(newProgress);

        if (newProgress >= 100) {
          setIsFiltering(false);
          updateExp9Part1({ filtered: true });
          completeStep('extraction-5');
        }
      }, 150);

      return () => clearInterval(timer);
    }
  }, [isFiltering, filterProgress]);

  // Handler: Weigh soil sample
  const handleWeighSoil = () => {
    if (!isActive(1)) return;
    setIsWeighing(true);
    setTimeout(() => {
      setIsWeighing(false);
      const mass = 0.9 + Math.random() * 0.2; // 0.9-1.1 g
      updateExp9Part1({
        soilMass: Number(mass.toFixed(4)),
        samplePrepared: true
      });
      completeStep('extraction-1');
    }, 1500);
  };

  // Handler: Transfer soil to beaker
  const handleTransferSoil = () => {
    if (!isActive(2) || !exp9Part1.samplePrepared) return;
    completeStep('extraction-2');
  };

  // Handler: Add HCl
  const handleAddAcid = () => {
    if (!isActive(3) || !isDone(2)) return;
    updateExp9Part1({ acidAdded: true });
    completeStep('extraction-3');
  };

  // Handler: Start heating
  const handleStartHeating = () => {
    if (!isActive(4) || !exp9Part1.acidAdded || exp9Part1.heated) return;
    updateExp9Part1({ heatingProgress: 1 });
  };

  // Handler: Filter
  const handleFilter = () => {
    if (!isActive(5) || !exp9Part1.heated || exp9Part1.filtered) return;
    setIsFiltering(true);
  };

  // Handler: Dilute
  const handleDilute = () => {
    if (!isActive(6) || !exp9Part1.filtered) return;
    updateExp9Part1({ diluted: true });
    completeStep('extraction-6');
  };

  const completedCount = completedSteps.filter((s: string) => s.startsWith('extraction')).length;

  // Calculate beaker color based on state
  const getBeakerColor = () => {
    if (!isDone(2)) return '#7DD3FC'; // Empty/water
    if (!exp9Part1.acidAdded) return '#8B7355'; // Soil brown
    return '#6B5344'; // Darker with acid
  };

  return (
    <div className="part1-extraction">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part I: Soil Extraction</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  {stepInfoContent[s.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(s.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Iron Extraction from Soil</h3>
          </div>
          <span className="bench-progress">{completedCount}/6 Complete</span>
        </div>

        <div className="bench-area">
          {/* Heating Progress */}
          {exp9Part1.heatingProgress > 0 && !exp9Part1.heated && (
            <div className="heating-progress">
              <Thermometer size={16} />
              <span>Extraction Progress:</span>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${exp9Part1.heatingProgress}%` }}
                />
              </div>
              <span>{Math.round(exp9Part1.heatingProgress)}%</span>
            </div>
          )}

          {/* Equipment Row 1: Balance and Soil */}
          <div className="equipment-row">
            <AnalyticalBalance
              reading={exp9Part1.soilMass || 0}
              isTared={true}
              onClick={handleWeighSoil}
              active={isActive(1) && !exp9Part1.samplePrepared}
              disabled={labMode === 'guided' && !isActive(1)}
            />

            <SoilContainer
              onClick={handleTransferSoil}
              active={isActive(2) && !isDone(2) && exp9Part1.samplePrepared}
              disabled={labMode === 'guided' && !isActive(2)}
            />

            {exp9Part1.soilMass && (
              <div className="mass-display">
                Sample: {exp9Part1.soilMass.toFixed(4)} g
              </div>
            )}
          </div>

          {/* Equipment Row 2: Beaker and Hot Plate */}
          <div className="equipment-row">
            <div className="beaker-hotplate">
              <Beaker
                size={500}
                fillLevel={isDone(2) ? 40 : 0}
                fillColor={getBeakerColor()}
                muddy={isDone(2) && !exp9Part1.filtered}
                label="Extraction"
                onClick={handleAddAcid}
                active={isActive(3) && !exp9Part1.acidAdded && isDone(2)}
                disabled={labMode === 'guided' && !isActive(3)}
              />

              <HotPlate
                isOn={exp9Part1.heatingProgress > 0 && !exp9Part1.heated}
                temperature={exp9Part1.heatingProgress > 0 ? Math.round(25 + exp9Part1.heatingProgress * 0.75) : 25}
                onClick={handleStartHeating}
                active={isActive(4) && !exp9Part1.heated && exp9Part1.acidAdded}
                disabled={labMode === 'guided' && !isActive(4)}
              />
            </div>

            {/* Acid Dropper */}
            {isDone(2) && !exp9Part1.acidAdded && (
              <div className="acid-container">
                <div className="acid-bottle" onClick={handleAddAcid}>
                  <Droplets size={24} />
                  <span>Conc. HCl</span>
                </div>
              </div>
            )}

            {/* Bubbles animation when heating */}
            {exp9Part1.heatingProgress > 0 && !exp9Part1.heated && (
              <div className="bubbles-indicator">
                <span>Simmering (15 min)...</span>
              </div>
            )}
          </div>

          {/* Equipment Row 3: Filtration */}
          <div className="equipment-row">
            <FiltrationApparatus
              isFiltering={isFiltering}
              progress={filterProgress}
              onClick={handleFilter}
              active={isActive(5) && !exp9Part1.filtered && exp9Part1.heated}
              disabled={labMode === 'guided' && !isActive(5)}
            />

            <VolumetricFlask
              volume={100}
              fillLevel={exp9Part1.diluted ? 100 : exp9Part1.filtered ? 50 : 0}
              fillColor="#D4A574"
              label="Extract"
              onClick={handleDilute}
              active={isActive(6) && !exp9Part1.diluted && exp9Part1.filtered}
              disabled={labMode === 'guided' && !isActive(6)}
            />
          </div>

          {/* Completion Message */}
          {exp9Part1.diluted && (
            <div className="completion-message">
              <Check size={20} />
              <span>Extraction complete! Ready for color development.</span>
            </div>
          )}

          {/* Bench Surface */}
          <div className="bench-surface" />
        </div>
      </div>

      {/* Info Box */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp9"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}

      <style>{`
        .part1-extraction {
          display: flex;
          gap: 16px;
          flex: 1;
          width: 100%;
          min-height: 0;
          padding: 16px;
        }

        .steps-panel {
          width: 240px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 12px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
          border: none;
          cursor: pointer;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          display: block;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 24px;
          gap: 24px;
          overflow-y: auto;
        }

        .heating-progress {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--bg-dark);
          padding: 12px 20px;
          border-radius: 10px;
          width: 100%;
          max-width: 500px;
        }

        .heating-progress .progress-bar {
          flex: 1;
          height: 8px;
          background: var(--border);
          border-radius: 4px;
          overflow: hidden;
        }

        .heating-progress .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #FF6432, #FF8050);
          transition: width 0.3s;
        }

        .heating-progress span {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .equipment-row {
          display: flex;
          gap: 32px;
          align-items: flex-end;
          flex-wrap: wrap;
          justify-content: center;
        }

        .mass-display {
          background: var(--bg-dark);
          padding: 8px 16px;
          border-radius: 8px;
          font-family: 'Courier New', monospace;
          font-size: 13px;
          color: var(--secondary);
        }

        .beaker-hotplate {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .acid-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .acid-bottle {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 12px 20px;
          background: var(--bg-elevated);
          border: 2px solid var(--primary);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          color: var(--danger);
        }

        .acid-bottle:hover {
          background: var(--primary);
          color: white;
        }

        .acid-bottle span {
          font-size: 12px;
          font-weight: 600;
        }

        .bubbles-indicator {
          position: absolute;
          padding: 6px 12px;
          background: rgba(255, 100, 50, 0.2);
          border-radius: 6px;
          font-size: 11px;
          color: var(--primary);
          animation: pulse 1s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(255, 90, 54, 0.3); }
          50% { box-shadow: 0 0 15px rgba(255, 90, 54, 0.5); }
        }

        .completion-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 20px;
          border-radius: 10px;
          font-size: 13px;
          background: rgba(0, 168, 107, 0.15);
          color: var(--secondary);
        }

        .bench-surface {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Part1Extraction;
